import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import EntornoMapa from './components/EntornoMapa';
import PlantaInterativa from './components/PlantaInterativa';
import CondominioScrub from './components/CondominioScrub';
import AgendarSofia from './components/AgendarSofia';
import ScrollHint from './components/ScrollHint';
import { initSofiaPixel } from './pixel';

const Page = styled.main`
  background: #0a070b;
  color: #f5e8c8;
  min-height: 100vh;
`;

const Hero = styled.section`
  position: relative;
  width: 100%;
  min-height: 100vh;
  background: #0a070b;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: hidden;
`;

const HeroVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

const HeroPoster = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
  pointer-events: none;
  transition: opacity 600ms ease;
  will-change: transform, opacity;
  animation: heroKenBurns 16s ease-in-out infinite alternate;

  @keyframes heroKenBurns {
    0%   { transform: scale(1.0)  translate3d(0, 0, 0); }
    100% { transform: scale(1.08) translate3d(-1%, -1%, 0); }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(10, 7, 11, 0.2) 0%,
    rgba(10, 7, 11, 0.0) 28%,
    rgba(10, 7, 11, 0.0) 65%,
    rgba(10, 7, 11, 0.25) 82%,
    rgba(10, 7, 11, 0.7) 94%,
    rgba(10, 7, 11, 1) 100%
  );
  pointer-events: none;
`;

const Interlude = styled.section`
  position: relative;
  width: 100%;
  background: #0a070b;
  padding: 10px 24px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;

  @media (min-width: 768px) {
    padding: 18px 24px 28px;
    gap: 8px;
  }
`;

const InterludeLine1 = styled.div`
  font-family: 'Playfair Display', 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-weight: 400;
  font-size: clamp(22px, 5.2vw, 36px);
  letter-spacing: 0.02em;
  color: #faf3e0;
  opacity: 0.85;
  line-height: 1;
  text-align: center;
`;

const InterludeLine2 = styled.div`
  font-family: 'Playfair Display', 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-weight: 400;
  font-size: clamp(40px, 10vw, 76px);
  letter-spacing: 0.015em;
  color: #e9c87a;
  line-height: 1;
  text-align: center;
  text-shadow: 0 0 30px rgba(233, 200, 122, 0.25);
`;

const Content = styled(motion.div)`
  position: relative;
  z-index: 2;
  text-align: center;
  /* padding-bottom extra reserva espaço pro botão flutuante (AgendarSofia) não
     tampar o subtítulo "Campo Belo · São Paulo". */
  padding: 0 24px 86px;

  @media (min-width: 768px) {
    padding-bottom: 104px;
  }
`;

const Eyebrow = styled.div`
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 0.45em;
  text-transform: uppercase;
  color: #e8d4a8;
  opacity: 0.75;
  margin-bottom: 28px;

  @media (min-width: 768px) {
    font-size: 12px;
  }
`;

const Title = styled.h1`
  font-family: 'Playfair Display', 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-weight: 400;
  font-size: clamp(80px, 24vw, 200px);
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #faf3e0;
  margin: 0;
  line-height: 0.85;
  text-shadow: 0 4px 50px rgba(0, 0, 0, 0.5);
`;

const Subtitle = styled.div`
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: #faf3e0;
  opacity: 0.8;
  margin-top: 36px;

  @media (min-width: 768px) {
    font-size: 13px;
  }
`;

export default function SofiaLP() {
  // Meta Pixel (conversão no site) — só inicializa nesta LP.
  useEffect(() => {
    initSofiaPixel();
  }, []);

  // Sempre iniciar no TOPO ao abrir/recarregar — sem scrollRestoration o browser
  // restaura a posição anterior e cai no meio do scrub do vídeo.
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
    return () => {
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'auto';
    };
  }, []);

  // Override App.css body overflow-x: hidden — quebra position: sticky nesta página.
  // Snap-type NÃO é global; é toggled via IntersectionObserver dentro do
  // PlantaInterativa (ativa quando showcases estão no viewport, desativa fora).
  useEffect(() => {
    const origBody = document.body.style.overflowX;
    document.body.style.overflowX = 'visible';
    return () => {
      document.body.style.overflowX = origBody;
    };
  }, []);

  // iOS autoplay: só ativa fallback img quando play() rejeitar OU 'playing' não
  // disparar em 5s. Não usa readyState — preload="metadata" deixa em 1, falso +.
  const videoRef = useRef(null);
  const [autoplayFailed, setAutoplayFailed] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;

    let succeeded = false;
    const log = (patch) => setDebugInfo((s) => ({ ...s, ...patch, t: Date.now() }));
    log({ mounted: true, ua: navigator.userAgent.slice(0, 50) });

    const onPlaying = () => {
      succeeded = true;
      log({ event: 'playing', readyState: v.readyState });
    };
    const onError = () => {
      log({ event: 'error', errorCode: v.error?.code, errorMsg: v.error?.message });
      setAutoplayFailed(true);
    };
    const onLoaded = () => log({ event: 'loadedmetadata', readyState: v.readyState });
    const onCanPlay = () => log({ event: 'canplay', readyState: v.readyState });
    v.addEventListener('playing', onPlaying);
    v.addEventListener('error', onError);
    v.addEventListener('loadedmetadata', onLoaded);
    v.addEventListener('canplay', onCanPlay);

    const p = v.play();
    if (p && typeof p.then === 'function') {
      p.then(() => log({ playPromise: 'resolved' })).catch((err) => {
        log({ playPromise: 'rejected', errName: err?.name, errMsg: err?.message });
        setAutoplayFailed(true);
      });
    } else {
      log({ playPromise: 'none' });
    }

    const t = setTimeout(() => {
      if (!succeeded) {
        log({ event: 'timeout-5s', paused: v.paused, readyState: v.readyState, currentTime: v.currentTime });
        setAutoplayFailed(true);
      }
    }, 5000);

    // Fallback final: se iOS bloquear autoplay (Low Power Mode etc.), o primeiro
    // gesto do user em qualquer lugar da página satisfaz a política e dispara
    // play(). Sem botão ▶ feio do iOS.
    const onFirstGesture = () => {
      if (succeeded || !videoRef.current) return cleanupGesture();
      const v2 = videoRef.current;
      v2.muted = true;
      const p2 = v2.play();
      if (p2 && typeof p2.then === 'function') {
        p2.then(() => {
          log({ event: 'gesture-resumed' });
          setAutoplayFailed(false);
        }).catch(() => {});
      }
      cleanupGesture();
    };
    const cleanupGesture = () => {
      window.removeEventListener('touchstart', onFirstGesture, true);
      window.removeEventListener('click', onFirstGesture, true);
      window.removeEventListener('scroll', onFirstGesture, true);
    };
    window.addEventListener('touchstart', onFirstGesture, { passive: true, capture: true });
    window.addEventListener('click', onFirstGesture, { capture: true });
    window.addEventListener('scroll', onFirstGesture, { passive: true, capture: true });

    return () => {
      clearTimeout(t);
      cleanupGesture();
      v.removeEventListener('playing', onPlaying);
      v.removeEventListener('error', onError);
      v.removeEventListener('loadedmetadata', onLoaded);
      v.removeEventListener('canplay', onCanPlay);
    };
  }, []);

  const debugEnabled = typeof window !== 'undefined' && window.location.search.includes('debug=1');

  return (
    <Page>
      <Hero>
        <HeroVideo
          ref={videoRef}
          autoPlay
          loop
          muted
          defaultMuted
          playsInline
          webkit-playsinline="true"
          x5-playsinline="true"
          preload="metadata"
          disableRemotePlayback
          disablePictureInPicture
          controls={false}
          poster="/sofia/01-hero.jpg"
          src="/sofia/01-hero-loop-seedance-v3.mp4"
        />
        {autoplayFailed && (
          <HeroPoster
            src="/sofia/01-hero.jpg"
            alt=""
            aria-hidden="true"
            style={{ opacity: 1 }}
          />
        )}
        {debugEnabled && (
          <div
            style={{
              position: 'fixed',
              top: 8,
              left: 8,
              right: 8,
              zIndex: 9999,
              padding: '8px 10px',
              background: 'rgba(0,0,0,0.85)',
              color: '#0f0',
              font: '10px/1.3 ui-monospace, Menlo, monospace',
              borderRadius: 6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              maxHeight: '40vh',
              overflow: 'auto',
            }}
          >
            {`autoplayFailed=${autoplayFailed}\n` + JSON.stringify(debugInfo, null, 2)}
          </div>
        )}
        <Overlay />
        <Content
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, ease: 'easeOut' }}
        >
          <Eyebrow>Lançamento · Nortis</Eyebrow>
          <Title>Sofia</Title>
          <Subtitle>Campo Belo · São Paulo</Subtitle>
        </Content>
      </Hero>
      <Interlude>
        <InterludeLine1>No coração do</InterludeLine1>
        <InterludeLine2>Campo Belo</InterludeLine2>
      </Interlude>
      <EntornoMapa />
      <PlantaInterativa />
      <CondominioScrub />
      <AgendarSofia />
      <ScrollHint />
    </Page>
  );
}
